import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { logger } from '@magnetar/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'
import { adImageBatchService } from '@/services/ad_image_batch_service'
import { Loader2 } from 'lucide-react'

interface CreateBatchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export default function CreateBatchDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateBatchDialogProps) {
  const { token } = useAuth()
  const { mode, id: viewModeId } = useViewMode()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    batch_name: '',
    description: '',
    overlay_interval_seconds: '60',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token || !formData.batch_name.trim()) return

    setIsLoading(true)
    try {
      // sys_admin 在 provider mode 時需要帶入 provider_id
      // provider_admin 可由 backend 自動取得
      const createData = {
        batch_name: formData.batch_name,
        description: formData.description,
        overlay_interval_ms: (parseInt(formData.overlay_interval_seconds, 10) || 0) * 1000,
        ...(mode === 'provider' && viewModeId ? { provider_id: viewModeId } : {}),
      }
      const result = await adImageBatchService.createBatch(createData, token)
      if (result.success) {
        onSuccess?.()
        setFormData({ batch_name: '', description: '', overlay_interval_seconds: '60' })
      }
    } catch (error) {
      logger.error('建立廣告圖片批次失敗', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>建立廣告圖片批次</DialogTitle>
            <DialogDescription>
              建立新的廣告圖片批次，之後可以編輯圖片項目和套用到診所
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="batch_name">批次名稱 *</Label>
              <Input
                id="batch_name"
                value={formData.batch_name}
                onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                placeholder="例如：週年慶廣告圖片"
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">描述</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="描述這個批次的用途..."
                rows={3}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="overlay_interval_seconds">覆蓋間隔（秒）*</Label>
              <Input
                id="overlay_interval_seconds"
                type="number"
                min="1"
                value={formData.overlay_interval_seconds}
                onChange={(e) =>
                  setFormData({ ...formData, overlay_interval_seconds: e.target.value })
                }
                required
                disabled={isLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              取消
            </Button>
            <Button type="submit" disabled={isLoading || !formData.batch_name.trim()}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              建立
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
